import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  TextField,
  Chip,
  Checkbox,
  useMediaQuery,
  Paper,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import useApi from '../../../utils/useApi';
import { formattedCurrency } from '../../../utils/utils';
import { generateItensMgvTxt, downloadMgvFile } from '../../../utils/mgvEngine';
import {
  SCALE_MODULES,
  getScaleModulePreference,
  setScaleModulePreference,
} from '../../../utils/scaleBarcodeParser';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
    minHeight: '80vh',
    maxHeight: '90vh',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      borderRadius: 0,
      minHeight: '100%',
      maxHeight: '100%',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#ffffff',
  },
  headerTitle: {
    fontWeight: 700,
    color: '#17497F',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  configBox: {
    backgroundColor: '#f0f7ff',
    border: '1px solid #cce3f9',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: '48vh',
    overflowY: 'auto',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    position: 'sticky',
    top: 0,
    zIndex: 2,
  },
  headCell: {
    fontWeight: 700,
    color: '#334155',
    fontSize: '0.82rem',
    whiteSpace: 'nowrap',
    padding: '10px 12px',
  },
  rowSelected: {
    backgroundColor: 'rgba(23, 73, 127, 0.04)',
  },
  pluBadge: {
    backgroundColor: '#17497F',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.75rem',
    borderRadius: 12,
    padding: '2px 8px',
    display: 'inline-block',
  },
  infoBanner: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fef3c7',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    color: '#92400e',
    fontSize: '0.85rem',
  },
  stickyFooter: {
    padding: '14px 24px',
    backgroundColor: '#fbfcfd',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
}));

const ScaleExportModal = ({
  open,
  handleClose,
  initialCategory = null,
  initialItems = null,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Carregamento de categorias
  const [categoriesLoad, categoriesInfo] = useApi({
    method: 'get',
    url: '/products/categories',
  });

  // Carregamento de produtos
  const [productsLoad, productsInfo] = useApi({
    method: 'get',
    url: '/products',
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Configurações globais de carga
  const [selectedModule, setSelectedModule] = useState(getScaleModulePreference);
  const [defaultShelfLife, setDefaultShelfLife] = useState(0);
  const [departmentCode, setDepartmentCode] = useState(1);

  // Sobrescritas locais por item: { [id]: { scaleType: '0' | '1', shelfLife: number } }
  const [itemOverrides, setItemOverrides] = useState({});

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Inicialização ao abrir o modal
  useEffect(() => {
    if (open) {
      categoriesLoad();

      if (initialItems && Array.isArray(initialItems) && initialItems.length > 0) {
        // Se vier com produtos pré-selecionados da listagem
        setProductsList(initialItems);
        setSelectedIds(initialItems.map((p) => p.id));
        setSelectedCategory(null);
      } else if (initialCategory) {
        // Se vier com uma categoria pré-selecionada
        setSelectedCategory(initialCategory);
        productsLoad({
          params: { category: initialCategory.id },
          onCompleted: (res) => {
            if (res && res.data) {
              setProductsList(res.data);
              setSelectedIds(res.data.map((p) => p.id));
            }
          },
        });
      } else {
        // Caso geral: carrega todos os produtos
        setSelectedCategory(null);
        productsLoad({
          onCompleted: (res) => {
            if (res && res.data) {
              setProductsList(res.data);
              // Pré-seleciona todos inicialmente para conveniência
              setSelectedIds(res.data.map((p) => p.id));
            }
          },
        });
      }
    } else {
      // Limpa estados ao fechar
      setProductsList([]);
      setSelectedIds([]);
      setItemOverrides({});
      setSearchTerm('');
      setPage(0);
    }
  }, [open, initialCategory, initialItems]);

  // Carregar produtos quando mudar a categoria selecionada manualmente no dropdown
  const handleCategoryChange = (event, newCategory) => {
    setSelectedCategory(newCategory);
    setPage(0);
    const params = newCategory ? { category: newCategory.id } : {};
    productsLoad({
      params,
      onCompleted: (res) => {
        if (res && res.data) {
          setProductsList(res.data);
          setSelectedIds(res.data.map((p) => p.id));
        }
      },
    });
  };

  // Determina o tipo de venda padrão para um produto
  const getItemScaleType = useCallback(
    (product) => {
      if (itemOverrides[product.id]?.scaleType !== undefined) {
        return itemOverrides[product.id].scaleType;
      }
      const u = String(product.unit || '').toLowerCase().trim();
      return u === 'kg' || u === 'g' || u === 'quilo' || u === 'kilo' ? '0' : '1';
    },
    [itemOverrides]
  );

  const handleToggleItemScaleType = (productId, newType) => {
    setItemOverrides((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        scaleType: newType,
      },
    }));
  };

  const handleBulkSetScaleType = (type) => {
    const updated = { ...itemOverrides };
    productsList.forEach((p) => {
      updated[p.id] = {
        ...updated[p.id],
        scaleType: type,
      };
    });
    setItemOverrides(updated);
  };

  // Filtro de busca textual na tabela
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return productsList;
    const term = searchTerm.toLowerCase().trim();
    return productsList.filter((p) => {
      const name = String(p.name || '').toLowerCase();
      const id = String(p.id || '');
      const barcode = String(p.barCode || '');
      return name.includes(term) || id.includes(term) || barcode.includes(term);
    });
  }, [productsList, searchTerm]);

  // Paginação dos itens filtrados
  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  // Seleção individual de checkbox
  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Seleção de todos os itens filtrados
  const handleSelectAllFiltered = () => {
    const filteredIds = filteredProducts.map((p) => p.id);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const isAllFilteredSelected =
    filteredProducts.length > 0 &&
    filteredProducts.every((p) => selectedIds.includes(p.id));
  const isSomeFilteredSelected =
    filteredProducts.some((p) => selectedIds.includes(p.id)) && !isAllFilteredSelected;

  // Ação de download da carga
  const handleDownloadCharge = () => {
    const selectedProducts = productsList.filter((p) => selectedIds.includes(p.id));

    if (selectedProducts.length === 0) return;

    // Enriquecer os produtos com as opções e sobrescritas do modal
    const preparedItems = selectedProducts.map((p) => ({
      ...p,
      scaleType: getItemScaleType(p),
      shelfLifeDays: defaultShelfLife,
      department: departmentCode,
    }));

    const fileContent = generateItensMgvTxt(preparedItems, {
      department: departmentCode,
      defaultShelfLifeDays: defaultShelfLife,
    });

    downloadMgvFile(fileContent, 'ITENSMGV.TXT');
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.header}>
        <Box>
          <Typography variant="h6" className={classes.headerTitle}>
            <FitnessCenterIcon style={{ color: '#17497F' }} />
            Carga de Balança - Padrão Toledo MGV
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Gere o arquivo <strong>ITENSMGV.TXT</strong> para importar no software MGV (Prix 4, Prix 5, Prix 6 Touch)
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ padding: '20px 24px' }}>
        {/* Barra superior de filtros e configurações globais */}
        <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={categoriesInfo.data || []}
              getOptionLabel={(option) => option.name || ''}
              value={selectedCategory}
              onChange={handleCategoryChange}
              loading={categoriesInfo.loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtrar por Categoria"
                  variant="outlined"
                  size="small"
                  placeholder="Todas as categorias"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Buscar por Nome ou Código"
              placeholder="Digite para filtrar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
            />
          </Grid>
        </Grid>

        {/* Parâmetros Globais de Carga */}
        <Paper className={classes.configBox} elevation={0}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="scale-module-select-label">Padrão da Balança</InputLabel>
                <Select
                  labelId="scale-module-select-label"
                  label="Padrão da Balança"
                  value={selectedModule}
                  onChange={(e) => {
                    const nextMod = e.target.value;
                    setSelectedModule(nextMod);
                    setScaleModulePreference(nextMod);
                  }}
                  style={{ fontSize: '0.85rem' }}
                >
                  {SCALE_MODULES.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="number"
                label="Validade (Dias)"
                value={defaultShelfLife}
                onChange={(e) => setDefaultShelfLife(Math.max(0, parseInt(e.target.value, 10) || 0))}
                helperText="0 = Não imprime"
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                type="number"
                label="Depto MGV"
                value={departmentCode}
                onChange={(e) => setDepartmentCode(Math.max(1, parseInt(e.target.value, 10) || 1))}
                helperText="Padrão: 01"
              />
            </Grid>
            <Grid item xs={12} sm={4} style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkSetScaleType('0')}
                style={{ fontSize: '0.75rem' }}
              >
                Todos Peso (kg)
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkSetScaleType('1')}
                style={{ fontSize: '0.75rem' }}
              >
                Todos Unid. (un)
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Produtos */}
        {productsInfo.loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <>
            <TableContainer className={classes.tableContainer}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow className={classes.tableHead}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        indeterminate={isSomeFilteredSelected}
                        checked={isAllFilteredSelected}
                        onChange={handleSelectAllFiltered}
                      />
                    </TableCell>
                    <TableCell className={classes.headCell} style={{ width: 80 }}>
                      PLU (ID)
                    </TableCell>
                    <TableCell className={classes.headCell}>Produto</TableCell>
                    <TableCell className={classes.headCell} style={{ width: 110 }}>
                      Preço Unit.
                    </TableCell>
                    <TableCell className={classes.headCell} style={{ width: 140 }}>
                      Tipo de Venda
                    </TableCell>
                    <TableCell className={classes.headCell} align="center" style={{ width: 80 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" style={{ padding: '30px' }}>
                        <Typography variant="body2" color="textSecondary">
                          Nenhum produto encontrado para os filtros selecionados.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedProducts.map((product) => {
                      const isSelected = selectedIds.includes(product.id);
                      const currentType = getItemScaleType(product);

                      return (
                        <TableRow
                          key={product.id}
                          hover
                          className={isSelected ? classes.rowSelected : ''}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isSelected}
                              onChange={() => handleToggleSelect(product.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <span className={classes.pluBadge}>
                              #{String(product.id).padStart(5, '0')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                            {product.barCode && (
                              <Typography variant="caption" color="textSecondary">
                                EAN: {product.barCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell style={{ fontWeight: 700, color: '#166534' }}>
                            {formattedCurrency(product.value || 0)}
                          </TableCell>
                          <TableCell>
                            <FormControl variant="outlined" size="small" style={{ minWidth: 115 }}>
                              <Select
                                value={currentType}
                                onChange={(e) =>
                                  handleToggleItemScaleType(product.id, e.target.value)
                                }
                                style={{ fontSize: '0.82rem', height: 32 }}
                              >
                                <MenuItem value="0">Peso (kg)</MenuItem>
                                <MenuItem value="1">Unidade (un)</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            {isSelected ? (
                              <Tooltip title="Incluído na carga">
                                <CheckCircleIcon style={{ color: '#10b981', fontSize: 20 }} />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                Ignorado
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredProducts.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}

        {/* Informação sobre como importar no MGV */}
        <Box mt={2}>
          <div className={classes.infoBanner}>
            <InfoOutlinedIcon style={{ fontSize: 20 }} />
            <span>
              Ao baixar o <strong>ITENSMGV.TXT</strong>, salve o arquivo na pasta de importação do MGV
              (geralmente <code>C:\Toledo\MGV6\Importa</code>). O serviço da Toledo transmitirá a carga para as balanças automaticamente.
            </span>
          </div>
        </Box>
      </DialogContent>

      <DialogActions className={classes.stickyFooter}>
        <Box>
          <Typography variant="body2" style={{ fontWeight: 700, color: '#334155' }}>
            {selectedIds.length} produto{selectedIds.length !== 1 ? 's' : ''} selecionado{selectedIds.length !== 1 ? 's' : ''} para carga
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Total de produtos disponíveis: {productsList.length}
          </Typography>
        </Box>

        <Box display="flex" gap={1}>
          <Button onClick={handleClose} color="default" variant="outlined">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            disabled={selectedIds.length === 0}
            onClick={handleDownloadCharge}
            style={{ fontWeight: 700 }}
          >
            Baixar Arquivo MGV
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ScaleExportModal;

